import { observer } from 'mobx-react-lite'
import { ComponentProps, FC, useEffect, useMemo, useRef } from 'react'
import SimplexNoise from 'simplex-noise'
import { styled } from '../../../lib/stitches'
import { IStudioStore } from '../../../stores/StudioStore'
import AudioVisualizer, { AudioVisualizationOptions } from '../AudioVisualizer'
import { useTimelineOverlay } from '../PlayerUtils'

const Container = styled('div', {
  display: 'flex',
  alignItems: 'stretch',
  position: 'relative',
  flex: 1,
  overflow: 'hidden'
})

type VisualizerWithOverlayProps = ComponentProps<typeof Container> & {
  audio: HTMLAudioElement
} & ComponentProps<typeof AudioVisualizer>

const VisualizerOverlay = styled('div', {
  position: 'absolute',
  background: 'transparent',
  left: '0px',
  top: '0px',
  bottom: '0px',
  right: '0px',
  transform: 'translateX(100%)',
  backdropFilter: 'grayscale(70%)',
  '&:before': {
    position: 'absolute',
    left: '0',
    right: '0',
    bottom: '0',
    top: '0',
    background: '$gray02',
    opacity: 0.7,
    content: ''
  }
})

const VisualizerWithOverlay: FC<VisualizerWithOverlayProps> = ({
  audio,
  audioBuffer,
  options,
  ...props
}) => {
  const overlayRef = useTimelineOverlay(audio)
  return (
    <Container {...props}>
      <VisualizerOverlay ref={overlayRef} />
      <AudioVisualizer audioBuffer={audioBuffer} options={options} />
    </Container>
  )
}

type MockVisualizerWithOverlayProps = {
  player?: IStudioStore
  seed?: string
  options?: Partial<AudioVisualizationOptions>
} & ComponentProps<typeof Container>

const randomSeed = () => (Math.random() * 1000).toString()

const DEFAULT_SIZE = 1000
const DEFAULT_SCALE = 4
export const generateNoise = (
  seed: string = randomSeed(),
  size = DEFAULT_SIZE,
  scale: number = DEFAULT_SCALE
): Float32Array => {
  const noise = new SimplexNoise(seed)
  const array = new Float32Array(size)
  for (let i = 0; i < size; i++) array[i] = noise.noise2D(i / scale, i / scale)
  return array
}

export const MockVisualizerWithOverlay: FC<MockVisualizerWithOverlayProps> = observer(
  ({ seed = randomSeed(), player, options, ...props }) => {
    const songLength16thNotes = player.song.songMintLength16Notes
    const progress = (player.playhead / songLength16thNotes) * 100

    const audioArray = useMemo(() => generateNoise(seed), [seed])
    const overlay = useRef<HTMLDivElement>(null)
    useEffect(() => {
      if (!overlay || typeof progress !== 'number') return
      overlay.current.style.transform = `translateX(${progress}%)`
    }, [progress])
    return (
      <Container {...props}>
        <VisualizerOverlay ref={overlay} />
        <AudioVisualizer audioArray={audioArray} options={options} />
      </Container>
    )
  }
)

export default VisualizerWithOverlay
